import React, { useEffect, useState } from 'react';
import './ChargePatientModal.scss';
import {
  formatPatientBalance,
  getBalanceColorClass,
} from 'src/legacy/utils/helpers';
import Input from 'src/components/base/Input/Input';
import AppButton from 'src/components/base/Button/Button';
import { useDispatch } from 'react-redux';
import { getUserDataById } from 'src/api/user';
import { User } from '../UserProfileHeader/types';
import { Spinner } from 'react-bootstrap';
import {
  chargeBillingAmount,
  chargeDirectoryBillingAmount,
  refundBillingAmount,
} from 'src/redux/services/billingServices';
import { setFlashNotification } from 'src/redux/modules/flashNotification';
import {
  DirectoryRow,
  filterKey,
  PatientRow,
  Transaction,
} from '../../features/billing-admin/billing-admin-home/BIllingAdminConstant';
import CustomModal from '../CustomModal/CustomModal';
import {
  ChargePatientModalType,
  ChargePatientRadioOptions,
} from './ChargePatientModalConstant';
import { useTranslation } from 'react-i18next';
import DropdownField from '../DropdownComponent/DropdownField';
import { Choices } from '../../types/TherapistAvailabilityWidget';
import { PaymentMethod } from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/StripePayment/StripePaymentInterface';
import Avatar from 'src/components/base/Avatar/Avatar';
import { amountRegex } from '../../utils';

interface ChargePatientModalProps {
  patientData: PatientRow | null;
  transactionData: Transaction | null;
  afterChargeSucess: () => void;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  paymentMethods?: PaymentMethod[] | null;
  directoryPatientData: DirectoryRow | null;
  isDirectoryPlatform: boolean;
}

const ChargePatientModal: React.FC<ChargePatientModalProps> = ({
  patientData,
  transactionData,
  afterChargeSucess,
  setShowModal,
  showModal,
  paymentMethods,
  directoryPatientData,
  isDirectoryPlatform,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('components');
  const [chargeValue] = useState<string>(
    patientData || directoryPatientData
      ? ChargePatientModalType?.CHARGE
      : transactionData
      ? ChargePatientModalType?.REFUND
      : '',
  );
  const [chargeAmount, setChargeAmount] = useState<string>(
    Math.abs(
      directoryPatientData
        ? directoryPatientData?.provider_balance
        : patientData
        ? patientData?.patient_balance
        : transactionData
        ? transactionData?.amount
        : 0,
    ).toString(),
  );
  const [errorAmount, setErrorAmount] = useState<string>();
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [userData, setUserData] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!showModal) {
      setDescription('');
      setErrorAmount('');
      setError(null);
    }
  }, [showModal, patientData, transactionData, directoryPatientData]);

  useEffect(() => {
    if (showModal) {
      fetchUserData();
    }
  }, [
    showModal,
    patientData?.client_id,
    transactionData?.clientId,
    directoryPatientData?.provider_id,
  ]);

  // Set default payment method when payment methods load
  useEffect(() => {
    if (paymentMethods) {
      const defaultMethod = paymentMethods?.find(
        (method: any) => method.is_default,
      );

      if (defaultMethod && !selectedPaymentMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      }
    }
  }, [paymentMethods]);

  const formatPaymentMethodsForDropdown = (): Choices[] => {
    if (!paymentMethods) return [];

    const activeCards = paymentMethods.filter(
      (method: PaymentMethod) => method.card_status === filterKey?.VALID_CARD,
    );

    const hasDefault = activeCards.some((m: PaymentMethod) => m.is_default);

    return activeCards.map((method: PaymentMethod) => ({
      label: (
        <span className="payment-method-option">
          <span className="payment-method-name">
            {method?.card_nick_name
              ? method?.card_nick_name
              : `${method.brand?.toUpperCase()}`}
          </span>
          <span className="payment-method-right">
            <span className="payment-method-separator">xx</span>
            <span className="payment-method-digits">
              {method?.last_4_digits}
            </span>
          </span>
        </span>
      ),
      searchText: `${method?.card_nick_name || method.brand} ${
        method.last_4_digits
      }`,
      value: method?.id,
      selected: hasDefault ? method?.is_default : false,
      unique_key: method?.id,
    }));
  };

  const handlePaymentMethodChange = (choice: Choices) => {
    if (choice.value) {
      setSelectedPaymentMethod(choice.value);
    }
  };

  const formatAmount = (value: string) => {
    if (!value) return '0.00';

    return parseFloat(value).toFixed(2);
  };

  const handleAmountBlur = () => {
    if (!chargeAmount) return;
    setChargeAmount(formatAmount(chargeAmount));
  };

  const handleAmountFocus = () => {
    if (chargeAmount === '0' || chargeAmount === '0.00') {
      setChargeAmount('');
    }
  };

  const fetchUserData = async () => {
    setDataLoading(true);
    setError(null);

    const userId =
      directoryPatientData?.provider_id ||
      patientData?.client_id ||
      (isDirectoryPlatform
        ? transactionData?.providerId
        : transactionData?.clientId);

    if (!userId) {
      setDataLoading(false);

      return;
    }

    try {
      const userData = await getUserDataById(userId, true, 'small');

      setUserData(userData);
    } catch (error: any) {
      setError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to load user information',
      );
    } finally {
      setDataLoading(false);
    }
  };

  const handleAmountChange = (value: string) => {
    setErrorAmount('');
    setError('');

    if (amountRegex?.test(value)) {
      if (
        value?.length > 1 &&
        value?.startsWith('0') &&
        !value?.startsWith('0.')
      ) {
        value = value?.replace(/^0+/, '');
      }

      setChargeAmount(value);
    }
  };

  const handleDiscription = (value: string) => {
    setDescription(value);
  };

  const onCharge = async () => {
    setLoading(true);
    setErrorAmount('');
    setError('');
    const formattedAmount = formatAmount(chargeAmount);
    const amount = Number(formattedAmount);

    if (!chargeAmount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setErrorAmount('Amount is required');
      setLoading(false);

      return;
    }

    try {
      if (transactionData && chargeValue === ChargePatientModalType?.REFUND) {
        const payload = {
          reason: description,
        };

        const transcationData = await refundBillingAmount(
          payload,
          transactionData?.id || '',
        );

        setError('');
        dispatch(
          setFlashNotification({
            message: transcationData?.message,
          }),
        );
        afterChargeSucess();
      } else if (
        patientData &&
        chargeValue === ChargePatientModalType?.CHARGE
      ) {
        const payload = {
          amount,
          currency: 'usd',
          description,
          serviceType: '',
          captureImmediately: true,
          paymentMethodId: selectedPaymentMethod,
        };

        const transcationData = await chargeBillingAmount(
          payload,
          patientData?.client_id || '',
        );

        setError('');
        dispatch(
          setFlashNotification({
            message: transcationData?.message,
            isSticky:
              transcationData?.message !==
              t('chargePatientModal.text.paymentSuccessful'),
          }),
        );
        afterChargeSucess();
      } else if (
        directoryPatientData &&
        chargeValue === ChargePatientModalType?.CHARGE
      ) {
        const payload = {
          amount,
          description,
          paymentMethodId: selectedPaymentMethod,
        };

        const transcationData = await chargeDirectoryBillingAmount(
          payload,
          directoryPatientData?.provider_id || '',
        );

        setError('');
        dispatch(
          setFlashNotification({
            message: transcationData?.message,
            isSticky:
              transcationData?.message !==
              t('chargePatientModal.text.providerPaymentSuccessful'),
          }),
        );
        afterChargeSucess();
      }

      setShowModal(false);
    } catch (error: any) {
      dispatch(
        setFlashNotification({
          message: error?.response?.data?.message || error?.message,
        }),
      );
      setError(error?.response?.data?.message || 'API FAILED');
    } finally {
      setErrorAmount('');
      setLoading(false);
    }
  };

  const onCancel = () => {
    setShowModal(false);
  };

  return (
    <CustomModal
      headerText={
        chargeValue === ChargePatientModalType?.CHARGE
          ? isDirectoryPlatform
            ? t('chargePatientModal.headerText.chargeProvider')
            : t('chargePatientModal.headerText.chargePatient')
          : isDirectoryPlatform
          ? t('chargePatientModal.headerText.refundProvider')
          : t('chargePatientModal.headerText.refundPatient')
      }
      showModal={showModal}
      setShowModal={setShowModal}
      dialogClassName="charge-patient-modal-dialog"
      headerClassName="charge-patient-modal-title"
      className="charge-patient-modal-body"
      iconClassName="charge-patient-modal-img"
    >
      {dataLoading ? (
        <div className="charge-patient-modal">
          <div className="charge-patient-modal-spinner d-flex justify-content-center align-items-center">
            <Spinner
              animation="border"
              role="status"
              variant="dark"
              className="mx-auto"
            />
          </div>
        </div>
      ) : (
        <div className="charge-patient-modal">
          <div className="charge-patient-modal-detail">
            <div className="charge-patient-modal-detail-info">
              {userData?.profile_pic && userData?.profile_pic?.length > 0 && (
                <div className="charge-patient-modal-detail-avatar">
                  <Avatar photoUrl={userData.profile_pic} size="38px" />
                </div>
              )}
              <div className="charge-patient-modal-detail-name">
                <div className="charge-patient-modal-detail-name-text">
                  {directoryPatientData &&
                  directoryPatientData?.provider_name &&
                  directoryPatientData?.provider_name?.length > 0
                    ? directoryPatientData?.provider_name
                    : patientData &&
                      patientData?.patient_name &&
                      patientData?.patient_name?.length > 0
                    ? patientData?.patient_name
                    : !isDirectoryPlatform &&
                      transactionData &&
                      transactionData?.clientName &&
                      transactionData?.clientName?.length > 0
                    ? transactionData?.clientName
                    : isDirectoryPlatform &&
                      transactionData &&
                      transactionData?.providerName &&
                      transactionData?.providerName?.length > 0
                    ? transactionData?.providerName
                    : 'N/A'}
                </div>
                <div className="charge-patient-modal-detail-name-phone">
                  {userData?.telephone_number}
                </div>
                <div className="charge-patient-modal-detail-name-phone">
                  {t('chargePatientModal.text.paymentPlan')}:{' '}
                  {directoryPatientData &&
                  directoryPatientData?.payment_plan &&
                  directoryPatientData?.payment_plan?.length > 0
                    ? directoryPatientData?.payment_plan
                    : patientData &&
                      patientData?.payment_plan &&
                      patientData?.payment_plan?.length > 0
                    ? patientData?.payment_plan
                    : transactionData &&
                      transactionData?.status &&
                      transactionData?.status?.length > 0
                    ? transactionData?.status
                    : 'N/A'}
                </div>
              </div>
            </div>
            <div className="charge-patient-modal-detail-balance">
              <div
                className={`charge-patient-modal-detail-balance-dollar ${
                  chargeValue === ChargePatientModalType?.CHARGE
                    ? getBalanceColorClass(
                        directoryPatientData
                          ? directoryPatientData?.provider_balance
                          : patientData
                          ? patientData?.patient_balance
                          : transactionData
                          ? transactionData?.amount
                          : 0,
                      )
                    : ''
                }`}
              >
                {formatPatientBalance(
                  directoryPatientData
                    ? directoryPatientData?.provider_balance
                    : patientData
                    ? patientData?.patient_balance
                    : transactionData
                    ? transactionData?.amount
                    : 0,
                )}
              </div>
              <div className="charge-patient-modal-detail-balance-text">
                {chargeValue === ChargePatientModalType?.CHARGE
                  ? t('chargePatientModal.text.patientBalance')
                  : t('chargePatientModal.text.transactionAmount')}
              </div>
            </div>
          </div>
          <div className="charge-patient-modal-amount">
            <span className="charge-patient-modal-amount-label">
              {chargeValue === ChargePatientModalType?.CHARGE
                ? ChargePatientRadioOptions?.CHARGE
                : ChargePatientRadioOptions?.REFUND}{' '}
              {t('chargePatientModal.text.amount')}
            </span>
            <Input
              name={`${
                chargeValue === ChargePatientModalType?.CHARGE
                  ? t('chargePatientModal.text.chargeAmount')
                  : t('chargePatientModal.text.refundAmount')
              }`}
              value={chargeAmount}
              onChange={(e: any) => handleAmountChange(e?.target?.value)}
              onBlur={handleAmountBlur}
              onFocus={handleAmountFocus}
              type="number"
              required={true}
              placeholder={`${
                chargeValue === ChargePatientModalType?.CHARGE
                  ? t('chargePatientModal.text.chargeAmount')
                  : t('chargePatientModal.text.refundAmount')
              }`}
              isInvalid={!!errorAmount}
              className={`mx-auto`}
              feedbackMessage={errorAmount}
              isCheckIcon={false}
              readOnly={chargeValue === ChargePatientModalType?.REFUND}
              step="0.01"
              inputMode="decimal"
            />
          </div>
          {chargeValue === ChargePatientModalType?.CHARGE &&
            paymentMethods &&
            paymentMethods?.length > 0 && (
              <div className="charge-patient-modal-amount">
                <span className="charge-patient-modal-amount-label">
                  {t('chargePatientModal.text.paymentMethod')}
                </span>
                <div className="charge-patient-modal-payment-method-dropdown">
                  <DropdownField
                    className="charge-patient-dropdown"
                    title={t('chargePatientModal.text.selectPaymentMethod')}
                    onChangeValue={handlePaymentMethodChange}
                    menu={formatPaymentMethodsForDropdown()}
                  />
                </div>
              </div>
            )}
          <div className="charge-patient-modal-description">
            <span className="charge-patient-modal-description-label">
              {t('chargePatientModal.text.addNote')}
            </span>
            <textarea
              rows={4}
              maxLength={500}
              placeholder="Add a note"
              value={description}
              onChange={e => handleDiscription(e.target?.value)}
            />
          </div>
          {error && (
            <div className="charge-patient-modal-err">{error && error}</div>
          )}
          <div className="charge-patient-modal-btn">
            <div>
              <AppButton
                className="close-btn wid-close btn-new-close"
                label={'Cancel'}
                onClick={onCancel}
                variant="secondary"
              />
            </div>
            <div>
              <AppButton
                className="delete-btn wid-add btn-new-primary"
                label={`${
                  chargeValue === ChargePatientModalType?.CHARGE
                    ? t('chargePatientModal.text.charge')
                    : t('chargePatientModal.text.refund')
                } $${formatAmount(chargeAmount)}`}
                onClick={onCharge}
                isProcessing={loading}
              />
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default ChargePatientModal;
